import React from "react";
import { Link } from "react-router-dom";
import { getBrand } from "../../utils/branding";
import { getLegalContent } from "../../utils/legalContent";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ArrowLeft, ReceiptText } from "lucide-react";

export default function Refund() {
  const brand = getBrand();
  const c = getLegalContent();

  // For free products (no payment), show a note
  if (!c.paymentSupported) {
    return (
      <div className="au-root" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="au-glass au-pad-lg au-center" style={{ maxWidth: 560, margin: 24 }}>
          <div className="au-badge" style={{ margin: "0 auto 20px", width: 56, height: 56, borderRadius: 16 }}>
            <ReceiptText />
          </div>
          <h1 style={{ fontSize: 36, margin: 0, marginBottom: 16 }}>Refund Policy</h1>
          <p className="au-lead" style={{ marginBottom: 24 }}>
            {c.fullName} is currently <strong style={{ color: "var(--au-accent-ink)" }}>free to use</strong> during beta. No payment, no refund policy needed.
          </p>
          <p style={{ fontSize: 14, color: "var(--au-muted)", marginBottom: 32 }}>
            When we introduce paid plans, this policy will be updated.
          </p>
          <Link to="/" className="au-pill au-pill-primary">
            <ArrowLeft /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Paid product (Aegis): full refund policy
  return (
    <div className="au-root">
      <header style={hdr}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
        <Link to="/" className="au-pill au-pill-glass au-pill-sm">
          <ArrowLeft /> Back to Home
        </Link>
      </header>

      <div className="au-container" style={{ maxWidth: 820 }}>
        <div className="au-glass au-pad-lg" style={{ marginBottom: 56 }}>
          <div className="au-eyebrow" style={{ marginBottom: 14 }}>
            REFUNDS · {c.lastUpdated.toUpperCase()}
          </div>
          <h1 style={h1}>Refund <span style={{ color: "var(--au-accent-ink)" }}>Policy</span></h1>
          <p className="au-lead" style={{ marginTop: 16 }}>
            We want you to love {c.fullName}. Here's how refunds work.
          </p>

          <S n="1" t="14-Day Money Back">
            <p>New paid subscriptions: <strong style={{ color: "var(--au-accent-ink)" }}>Full refund within 14 days</strong> of first payment, no questions asked.</p>
            <ul style={ul}>
              <li>Applies to first-time subscribers only</li>
              <li>Email <a href={`mailto:${c.contactEmail}`} style={link}>{c.contactEmail}</a> with subject "Refund Request"</li>
              <li>Refund processed within 5-7 business days</li>
              <li>Account access continues until end of paid period</li>
            </ul>
          </S>

          <S n="2" t="Pro-rated Refunds">
            <p>After 14 days, refunds calculated pro-rata for unused months on annual plans.</p>
            <ul style={ul}>
              <li>Annual plan, cancel after 6 months → 6 months refunded</li>
              <li>Monthly plans: no pro-rating, current month not refunded</li>
              <li>Cancel anytime — no termination fees</li>
            </ul>
          </S>

          <S n="3" t="Non-Refundable">
            <ul style={ul}>
              <li>Custom enterprise contracts (per separate agreement)</li>
              <li>Add-on services already delivered</li>
              <li>Accounts terminated for AUP violations</li>
              <li>Trial periods (already free)</li>
            </ul>
          </S>

          <S n="4" t="How to Cancel">
            <ol style={ul}>
              <li>Login to your dashboard</li>
              <li>Go to Settings → Billing</li>
              <li>Click "Cancel Subscription"</li>
              <li>Service continues until end of current paid period</li>
            </ol>
          </S>

          <S n="5" t="Failed Payments">
            <ul style={ul}>
              <li>3 retry attempts over 7 days</li>
              <li>Account paused (not deleted) on failure</li>
              <li>Reactivate anytime by paying outstanding amount</li>
              <li>Data preserved for 60 days after suspension</li>
            </ul>
          </S>

          <S n="6" t="Disputes">
            <p>Have a billing concern? Contact us first:</p>
            <ul style={ul}>
              <li>Email: <a href={`mailto:${c.contactEmail}`} style={link}>{c.contactEmail}</a></li>
              <li>Response within 1 business day</li>
              <li>Most disputes resolved within 48 hours</li>
              <li>For payment gateway issues: contact Razorpay/Stripe directly</li>
            </ul>
          </S>

          <S n="7" t="Contact">
            <p>Questions? Email <a href={`mailto:${c.contactEmail}`} style={link}>{c.contactEmail}</a></p>
            <p style={metaMuted}>{c.fullName} · {c.address}</p>
          </S>

        </div>
      </div>
      <Footer />
    </div>
  );
}

function S({ n, t, children }) {
  return (
    <div style={section}>
      <h2 style={h2}>
        <span style={{ color: "var(--au-accent-ink)", marginRight: 10 }}>{n}.</span>{t}
      </h2>
      <div style={body}>{children}</div>
    </div>
  );
}

const hdr = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, maxWidth: 1200, margin: "0 auto", padding: "28px 24px 8px" };
const h1 = { fontSize: 42, margin: 0 };
const section = { marginTop: 36, paddingTop: 28, borderTop: "1px solid var(--au-hair)" };
const h2 = { fontSize: 20, marginBottom: 16, fontWeight: 700 };
const body = { color: "var(--au-text-2)", fontSize: 14, lineHeight: 1.8 };
const ul = { margin: "8px 0", paddingLeft: 22 };
const link = { color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 600 };
const metaMuted = { fontSize: 13, color: "var(--au-muted)", marginTop: 8 };
const copyright = { marginTop: 48, paddingTop: 24, borderTop: "1px solid var(--au-hair)", fontSize: 12, color: "var(--au-muted)", textAlign: "center" };
